import { Component, Input, OnDestroy, OnInit, EventEmitter, Output, ChangeDetectorRef } from '@angular/core';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { UploadStateStyleEnum } from 'app/shared/upload-file/upload-file.enum';
import { UploadFileService } from 'app/shared/upload-file/upload-file.service';
import { FileService } from 'app/shared/file.service';
import { ToastrService } from 'ngx-toastr';
import { saveAs } from 'file-saver';
import { Subscription } from 'rxjs-compat';
import { DynamicLabelManagerSections } from '../users-setting.constant';
import { LabelType } from 'app/API.service';
import * as ExcelJS from 'exceljs';
import { UsersSettingsService } from '../users-settings.service';
import { UtilsService } from 'app/shared/utils.service';

export interface IUploadResult {
  failedItems: any[];
  cleared: number;
  failed: number;
}

@Component({
  selector: 'cygov-bulk-dynamic-label-upload',
  templateUrl: './bulk-dynamic-label-upload.component.html',
  styleUrls: ['./bulk-dynamic-label-upload.component.scss'],
  standalone: false,
})
export class BulkDynamicLabelUploadComponent implements OnInit, OnDestroy {
  @Output() data = new EventEmitter<any[]>();
  @Output() closePopUp = new EventEmitter<boolean>();
  @Input() type: LabelType = LabelType.GENERAL;
  @Input() entityId: string;
  @Input() defaultCategories: any[] = [];

  uploadStateEnum = UploadStateStyleEnum;
  requiredInfo: { fileType: string } = { fileType: 'DYNAMIC_LABELS_FILE' };
  subscriptions: Subscription[] = [];
  uploadResult: IUploadResult | Record<string, any> = {
    failedItems: [],
    cleared: 0,
    failed: 0,
  };

  dynamicLabelManagerMapper: any = {
    categories: { headers: [], values: [], isSelectAll: false },
    tags: { headers: [], values: [], isSelectAll: false },
  };

  nextStepBtnText = 'NEXT';
  disableBtn = false;
  disableBackBtn = false;
  invalidFileData = false;
  closeModalInstance = null;
  uploadedFile: File | null = null;
  filteredDynamicLabelManagerItems: any = {};

  steps = [
    { num: 'first', title: 'Download Template', active: true, completed: false },
    { num: 'Second', title: 'Upload Template', active: false, completed: false },
    { num: 'Third', title: 'Results', active: false, completed: false },
  ];

  ngbModalOptions: NgbModalOptions = {
    size: 'sm',
    windowClass: 'modal-template-cont dynamic-label-bulk-upload-popup-window',
    backdropClass:
      'modal-template-cont-backdrop dynamic-label-bulk-upload-popup-backdrop-modal md-modal-template-backdrop',
    backdrop: 'static',
  };

  newTagValue: string = '';

  searchText: string = '';
  isAddEnabled: boolean;

  dynamicLabelManagerSections = [];
  isEditMode = false;

  currentTab = '';
  headerNotToRead = ['Instructions'];
  showAddBox: boolean;
  showFailedItemsModal: boolean;
  constructor(
    private toastrService: ToastrService,
    private uploadService: UploadFileService,
    private cdRef: ChangeDetectorRef,
    private fileService: FileService,
    private userSettingsService: UsersSettingsService
  ) {}

  ngOnInit(): void {
    // Initialize the neceassary data for the component.
    this.initializeData();

    // Populating the Tabs Data.
    this.populateTabsData();

    // Subscribing to the file upload event.
    this.subscriptions.push(
      this.uploadService.getFile().subscribe(file => {
        if (!file || Array.isArray(file)) {
          return;
        }
        this.uploadedFile = file;
        setTimeout(() => {
          this.parseFileData();
        }, 50);
      })
    );

    // Fetching the list of only parent categories from the default categories.
    this.defaultCategories = this.defaultCategories.filter(category => !category.categoryId);
    this.defaultCategories = this.defaultCategories.map(category => ({
      name: category.name,
      value: category.name,
    }));
  }

  initializeData(): void {
    this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
    this.currentTab = 'categories';
    this.uploadResult.failedItems ||= [];
    this.uploadResult.cleared = 0;
    this.uploadResult.failed = 0;
    this.showAddBox = false;
    this.newTagValue = '';
    this.isAddEnabled = false;

    this.isEditMode = false;
    this.dynamicLabelManagerMapper.tags = { headers: [], values: [], isSelectAll: false };
    this.dynamicLabelManagerMapper.categories = { headers: [], values: [], isSelectAll: false };
    this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
    this.currentTab = 'categories';
  }

  populateTabsData(): void {
    switch (this.type) {
      case LabelType.POLICY: {
        this.dynamicLabelManagerSections = [
          { name: DynamicLabelManagerSections.POLICY_CATEGORIES, isSelected: true },
          { name: DynamicLabelManagerSections.POLICY_TAGS, isSelected: false },
        ];
        break;
      }
      case LabelType.RISK: {
        this.dynamicLabelManagerSections = [
          { name: DynamicLabelManagerSections.RISK_CATEGORIES, isSelected: true },
          { name: DynamicLabelManagerSections.RISK_TAGS, isSelected: false },
        ];
        break;
      }
      case LabelType.GENERAL: {
        this.dynamicLabelManagerSections = [
          { name: DynamicLabelManagerSections.GENERAL_CATEGORIES, isSelected: true },
          { name: DynamicLabelManagerSections.GENERAL_TAGS, isSelected: false },
        ];
        break;
      }
    }
  }

  moveToNextStep(): void {
    const currentStepIndex = this.steps.findIndex(s => s.active);
    switch (currentStepIndex) {
      case 0: {
        this.disableBtn = true;
        break;
      }
      case 1: {
        if (
          !this.dynamicLabelManagerMapper.categories.values.length &&
          !this.dynamicLabelManagerMapper.tags.values.length
        ) {
          this.toastrService.error('Please upload a file before proceeding');
          return;
        }
        this.nextStepBtnText = 'ADD';
        this.disableBtn = false;
        break;
      }

      case 2: {
        this.disableBtn = false;
        this.nextStepBtnText = 'NEXT';
        this.uploadService.resetEverything();
        this.data.emit(this.filteredDynamicLabelManagerItems);
        break;
      }
    }

    if (this.steps[currentStepIndex + 1]) {
      this.steps[currentStepIndex].active = false;
      this.steps[currentStepIndex].completed = true;
      this.steps[currentStepIndex + 1].active = true;
    }
  }

  moveBackToOldStep(): void {
    const currentStepIndex = this.steps.findIndex(s => s.active);

    if (this.steps[currentStepIndex - 1]) {
      this.steps[currentStepIndex].active = false;
      this.steps[currentStepIndex - 1].active = true;
      this.steps[currentStepIndex - 1].completed = false;
    }

    switch (currentStepIndex - 1) {
      case 0: {
        this.disableBtn = false;
        this.nextStepBtnText = 'NEXT';
        this.uploadService.resetEverything();
        break;
      }
      case 1: {
        this.disableBtn = true;
        this.nextStepBtnText = 'NEXT';
        this.uploadService.resetEverything();
        this.invalidFileData = false;
        this.dynamicLabelManagerMapper.tags = { headers: [], values: [] };
        this.dynamicLabelManagerMapper.categories = { headers: [], values: [] };
        this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
        this.uploadResult.failedItems = [];
        this.uploadResult.cleared = 0;
        this.uploadResult.failed = 0;
        break;
      }
    }
  }

  resetPopupState(): void {
    this.steps.forEach((step, index) => {
      step.active = !index;
      step.completed = false;
    });
    this.nextStepBtnText = 'NEXT';
    this.uploadResult = {};
    this.disableBtn = false;
    this.dynamicLabelManagerMapper.tags = { headers: [], values: [], isSelectAll: false };
    this.dynamicLabelManagerMapper.categories = { headers: [], values: [], isSelectAll: false };
    this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
    this.currentTab = 'categories';
    this.isAddEnabled = false;
    this.isEditMode = false;
  }

  resetAndClosePopup(): void {
    this.resetPopupState();
    this.uploadService.resetEverything();
    if (this.closeModalInstance) {
      this.closeModalInstance.close();
    }
    this.closePopUp.next(true);
  }

  async downloadCSVTemplate(): Promise<void> {
    try {
      let fileName = '';
      switch (this.type) {
        case LabelType.GENERAL: {
          fileName = 'General_Tags';
          break;
        }
        case LabelType.RISK: {
          fileName = 'Risk_Tags';
          break;
        }
        case LabelType.POLICY: {
          fileName = 'Policy_Management';
          this.toastrService.info('Please wait while we process the template ... this may take a few seconds.');
          await this.processPolicyTemplate(fileName);
          this.toastrService.success('Template downloaded successfully');
          return; // Exit early as processing is handled separately          break;
        }
      }
      const Path = `DYNAMIC_LABEL_TEMPLATES/${fileName}.xlsx`;
      const url = await this.fileService.downloadFileFromS3(Path);
      const response = await fetch(url);
      const blob = await response.blob();
      saveAs(blob, `${fileName}.xlsx`);
      this.toastrService.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template', error);
      this.toastrService.error('Error downloading template');
    }
  }

  async processPolicyTemplate(fileName: string): Promise<void> {
    try {
      // Step 1: Download the template from S3
      const Path = `DYNAMIC_LABEL_TEMPLATES/${fileName}.xlsx`;
      const url = await this.fileService.downloadFileFromS3(Path);
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();

      // Step 2: Load the workbook using ExcelJS
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      // Step 3: Get the specific worksheet
      const worksheetName = 'Policy Categories&Sub Categorie';
      const worksheet = workbook.getWorksheet(worksheetName);

      if (!worksheet) {
        throw new Error(`Worksheet "${worksheetName}" not found`);
      }

      // Step 4: Fetch categories from DB
      const dbCategoryNames = await this.getGlobalCategoriesByEntityId(this.entityId);

      // Step 7: Find the "Policy Categories" column and get existing dropdown values
      const headerRow = worksheet.getRow(1); // Assuming headers are in row 1
      let policyCategoriesColIndex: number | null = null;

      headerRow.eachCell((cell, colNumber) => {
        if (cell.value === 'Policy Categories') {
          policyCategoriesColIndex = colNumber;
        }
      });

      if (!policyCategoriesColIndex) {
        throw new Error('Policy Categories column not found');
      }

      // Step 8: Get existing dropdown values from data validation
      const existingDropdownValues: string[] = this.getExistingDropdownValues(worksheet, policyCategoriesColIndex);

      // Step 9: Merge and remove duplicates
      const mergedCategories = [...new Set([...existingDropdownValues, ...dbCategoryNames])];

      // Step 10: Update data validation for the "Policy Categories" column
      await this.processXLSXFile(workbook, worksheet, policyCategoriesColIndex, mergedCategories, fileName);
    } catch (error) {
      console.error('Error processing policy template', error);
      this.toastrService.error('Error downloading template');
    }
  }

  private getExistingDropdownValues(worksheet: ExcelJS.Worksheet, policyCategoriesColIndex: number): any {
    const existingDropdownValues: any[] = [];
    // Get the first data row (row 2) to check for data validation
    const firstDataRow = worksheet.getRow(2);
    const cell = firstDataRow.getCell(policyCategoriesColIndex);

    if (cell.dataValidation && cell.dataValidation.type === 'list') {
      if (cell.dataValidation.formulae && cell.dataValidation.formulae.length > 0) {
        // Parse the formula (e.g., "Value1,Value2,Value3")
        const formula = cell.dataValidation.formulae[0];
        // Remove quotes and split by comma
        const values = formula
          .replace(/"/g, '')
          .split(',')
          .map(v => v.trim());
        existingDropdownValues.push(...values);
      }
    }
    return existingDropdownValues;
  }

  async processXLSXFile(
    workbook: ExcelJS.Workbook,
    worksheet: ExcelJS.Worksheet,
    policyCategoriesColIndex: number,
    mergedCategories: any[],
    fileName: string
  ): Promise<void> {
    try {
      const lastRow = 10000;
      for (let rowNum = 2; rowNum <= lastRow; rowNum++) {
        const cell = worksheet.getCell(rowNum, policyCategoriesColIndex);
        cell.dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [`"${mergedCategories.join(',')}"`],
        };
      }

      // Step 11: Write the modified workbook to a buffer
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Step 12: Download the modified file
      saveAs(blob, `${fileName}.xlsx`);
      this.toastrService.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error processing XLSX file', error);
      this.toastrService.error('Error processing XLSX file');
    }
  }

  async getGlobalCategoriesByEntityId(entityId: string): Promise<any[]> {
    try {
      const categoriesResponse = await this.userSettingsService.getGlobalCategoriesByEntityId(
        this.entityId,
        LabelType.POLICY
      );
      const parentCategories = categoriesResponse.filter(category => !category.categoryId);
      const dbCategoryNames = parentCategories.map(category => category.name || category.categoryName); // Adjust property name as needed
      return dbCategoryNames;
    } catch (error) {
      console.error('Error getting global categories by entity id', error);
      this.toastrService.error('Error getting global categories by entity id');
    }
  }

  // ================================================
  // ============== Private Methods =================
  // ================================================
  private parseFileData(): void {
    // Mock file processing - simulate parsing and validation
    this.disableBtn = true;
    if (!this.uploadedFile || Array.isArray(this.uploadedFile)) {
      return;
    }

    this.disableBtn = true;
    const jsonData: any[] = [];
    const reader = new FileReader();

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const buffer = e.target?.result as ArrayBuffer;
      const workbook = new ExcelJS.Workbook();

      await workbook.xlsx.load(buffer);

      if (!workbook.worksheets.length) {
        this.toastrService.info('No worksheets found in the uploaded file. Please check the file and try again.');
        return;
      }

      let headerslist: string[] = [];

      // We need to iterate over the list of worksheets and then populate the data accordingly.
      workbook.worksheets.forEach(worksheet => {
        const headerRow = worksheet.getRow(1);
        const headers = (Array.isArray(headerRow.values) ? headerRow.values : [])
          .slice(1)
          .map(h => (typeof h === 'string' ? h.trim() : ''));
        if (!this.headerNotToRead.includes(worksheet.name)) {
          headerslist = headerslist.concat(headers);
          this.populateTableHeaders(headers);
          worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
            if (rowNumber === 1) {
              return;
            } // skip header
            const rowData: any = {};
            headers.forEach((header, colIndex) => {
              rowData[header] = row.getCell(colIndex + 1).value;
            });
            jsonData.push(rowData);
          });
        }
      });

      // We need to check the validation of the column headers in file first.
      if (!this.checkValidationOfFile(jsonData, headerslist)) {
        return;
      }

      this.settingEqualWidthForHeaders();
      if (this.type === LabelType.POLICY) {
        this.updateDefaultCategories(jsonData);
      }
      this.populatingMapperData(jsonData);
      this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
      // Compute counts after the filtered view is hydrated so the
      // Upload Results header is correct on first render too.
      this.calculateFailedItemsAndClearedItems();
      if ((this.uploadResult?.failed || 0) > 0) {
        this.toastrService.error('Please clear the failed items before saving.');
      }
      this.disableBtn = false;
      this.moveToNextStep();
      this.cdRef.detectChanges();
    };

    reader.readAsArrayBuffer(this.uploadedFile);
  }

  settingEqualWidthForHeaders(): void {
    // Setting the equal width for all headers.
    if (this.dynamicLabelManagerMapper.categories.headers.length > 1) {
      this.dynamicLabelManagerMapper.categories.headers.forEach(header1 => {
        header1.width = '100%';
      });
    } else {
      this.dynamicLabelManagerMapper.categories.headers[0].width = '50%';
    }
    if (this.dynamicLabelManagerMapper.tags.headers.length > 1) {
      this.dynamicLabelManagerMapper.tags.headers.forEach(header2 => {
        header2.width = '100%';
      });
    } else {
      this.dynamicLabelManagerMapper.tags.headers[0].width = '50%';
    }
  }

  updateDefaultCategories(jsonData: any[]): void {
    if (jsonData.length > 0) {
      jsonData.forEach(item => {
        Object.keys(item).forEach(key => {
          if (key === DynamicLabelManagerSections.POLICY_CATEGORIES) {
            // First check if the category is already in the default categories.
            if (!this.defaultCategories.some(category => category.value === item[key])) {
              this.defaultCategories.push({ name: item[key], value: item[key] });
            }
          }
        });
      });
    }
  }

  // ---------------------------------------------------------------------------------
  // ============== Helper Methods For Table Headers=================
  // ---------------------------------------------------------------------------------
  isCategoryHeader(header: any): boolean {
    return [
      DynamicLabelManagerSections.GENERAL_CATEGORIES,
      DynamicLabelManagerSections.RISK_CATEGORIES,
      DynamicLabelManagerSections.POLICY_CATEGORIES,
      DynamicLabelManagerSections.POLICY_SUB_CATEGORIES,
    ].includes(header);
  }

  isTagHeader(header: any): boolean {
    return [
      DynamicLabelManagerSections.GENERAL_TAGS,
      DynamicLabelManagerSections.RISK_TAGS,
      DynamicLabelManagerSections.POLICY_TAGS,
    ].includes(header);
  }

  populateTableHeaders(headers: string[]): void {
    // Check if Header is of type Category or Tag.
    headers.forEach(header => {
      if (this.isCategoryHeader(header)) {
        this.dynamicLabelManagerMapper.categories.headers.push({ name: header, isSortable: false, width: '50%' });
      } else if (this.isTagHeader(header)) {
        this.dynamicLabelManagerMapper.tags.headers.push({ name: header, isSortable: false, width: '50%' });
      }
    });
  }

  // ---------------------------------------------------------------------------------
  // ============== Helper Methods For Populating Mapper Data=================
  // ---------------------------------------------------------------------------------
  populatingMapperData(jsonData: any[]): void {
    jsonData.forEach((item: any, index: number) => {
      if (
        DynamicLabelManagerSections.GENERAL_CATEGORIES in item ||
        DynamicLabelManagerSections.RISK_CATEGORIES in item ||
        DynamicLabelManagerSections.POLICY_CATEGORIES in item
      ) {
        this.dynamicLabelManagerMapper.categories.values.push({ ...item, isSelected: false });
        if (item[DynamicLabelManagerSections.POLICY_CATEGORIES] === null) {
          item.rowNumber = index + 1;
        }
      }

      if (
        DynamicLabelManagerSections.RISK_TAGS in item ||
        DynamicLabelManagerSections.GENERAL_TAGS in item ||
        DynamicLabelManagerSections.POLICY_TAGS in item
      ) {
        this.dynamicLabelManagerMapper.tags.values.push({ ...item, isSelected: false });
      }
    });
  }

  checkValidationOfFile(jsonData: any[], headerslist: string[]): boolean {
    if (!jsonData.length) {
      this.disableBtn = false;
      this.nextStepBtnText = 'NEXT';
      this.uploadService.resetEverything();
      this.invalidFileData = false;
      this.toastrService.error('The uploaded file is empty');
      return false;
    }

    const fileKeys = Object.keys(jsonData[0]);
    let requiredColumns: string[] = [];

    switch (this.type) {
      case LabelType.POLICY: {
        requiredColumns = [
          DynamicLabelManagerSections.POLICY_TAGS,
          DynamicLabelManagerSections.POLICY_CATEGORIES,
          DynamicLabelManagerSections.POLICY_SUB_CATEGORIES,
        ];
        break;
      }
      case LabelType.RISK: {
        requiredColumns = [DynamicLabelManagerSections.RISK_TAGS, DynamicLabelManagerSections.RISK_CATEGORIES];
        break;
      }
      case LabelType.GENERAL: {
        requiredColumns = [DynamicLabelManagerSections.GENERAL_TAGS, DynamicLabelManagerSections.GENERAL_CATEGORIES];
        break;
      }
    }

    const missingColumns = requiredColumns.filter(column => !headerslist.includes(column));

    if (missingColumns.length > 0) {
      this.disableBtn = false;
      this.nextStepBtnText = 'NEXT';
      this.uploadService.resetEverything();
      this.invalidFileData = false;
      missingColumns.forEach(column => {
        this.toastrService.error(`The uploaded file is missing required column: ${column}`);
      });
      return false;
    } else {
      return true;
    }
  }

  addNewData(tab: string): void {
    // Need to populate the headers as Keys values too and then push the new data.
    const headers = this.dynamicLabelManagerMapper[tab].headers.map(header => header.name);
    const newData = {};
    headers.forEach(header => {
      newData[header] = '';
    });
    this.filteredDynamicLabelManagerItems[tab].values.unshift(newData);
    // this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
  }

  searchDynamicLabelManagerItems(term: any): void {
    if (term) {
      this.filteredDynamicLabelManagerItems[this.currentTab].values = JSON.parse(
        JSON.stringify(
          this.dynamicLabelManagerMapper[this.currentTab].values.filter(item => {
            // Return the result of .some() to the filter callback
            return Object.values(item).some((value: any) => {
              // Only check string values
              if (typeof value === 'string') {
                return value.toLowerCase().includes(term.toLowerCase());
              }
              return false;
            });
          })
        )
      );
    } else {
      this.filteredDynamicLabelManagerItems[this.currentTab].values = JSON.parse(
        JSON.stringify(this.dynamicLabelManagerMapper[this.currentTab].values)
      );
    }
  }

  clearUploadResults(): void {
    const itemsToRemove = this.filteredDynamicLabelManagerItems[this.currentTab].values.filter(item => item.isSelected);

    // Remove all selected items
    this.filteredDynamicLabelManagerItems[this.currentTab].values = JSON.parse(
      JSON.stringify(this.filteredDynamicLabelManagerItems[this.currentTab].values.filter(item => !item.isSelected))
    );
    this.dynamicLabelManagerMapper[this.currentTab].values = JSON.parse(
      JSON.stringify(this.filteredDynamicLabelManagerItems[this.currentTab].values)
    );
    this.filteredDynamicLabelManagerItems[this.currentTab].isSelectAll = JSON.parse(
      JSON.stringify(this.dynamicLabelManagerMapper[this.currentTab].values.every(item => item.isSelected))
    );
    // Recalculate so the header reflects the post-removal counts for the active tab
    if (itemsToRemove.length) {
      this.calculateFailedItemsAndClearedItems();
    }
  }

  sortDynamicLabelManagerItems(headerName: string, isSortable: boolean): void {
    this.filteredDynamicLabelManagerItems[this.currentTab].headers.find(
      header => header.name === headerName
    ).isSortable = !isSortable;
    this.filteredDynamicLabelManagerItems[this.currentTab].values.sort((a: any, b: any) => {
      if (isSortable) {
        return a[headerName].localeCompare(b[headerName]);
      } else {
        return b[headerName].localeCompare(a[headerName]);
      }
    });
  }

  toggleSection(section: any): void {
    section.isSelected = !section.isSelected;
    for (const data of this.dynamicLabelManagerSections) {
      if (data.name !== section.name) {
        data.isSelected = false;
      }
    }

    this.filteredDynamicLabelManagerItems[this.currentTab].isSelectAll = false;
    this.filteredDynamicLabelManagerItems[this.currentTab].values = JSON.parse(
      JSON.stringify(this.dynamicLabelManagerMapper[this.currentTab].values)
    );
    this.searchText = '';

    if (
      [
        DynamicLabelManagerSections.POLICY_CATEGORIES,
        DynamicLabelManagerSections.RISK_CATEGORIES,
        DynamicLabelManagerSections.GENERAL_CATEGORIES,
      ].includes(section.name)
    ) {
      this.currentTab = 'categories';
    } else {
      this.currentTab = 'tags';
    }

    // Upload results header should reflect only the active tab's counts
    this.calculateFailedItemsAndClearedItems();
  }

  selectItem(item: any): void {
    item.isSelected = !item.isSelected;
    this.filteredDynamicLabelManagerItems[this.currentTab].isSelectAll = this.filteredDynamicLabelManagerItems[
      this.currentTab
    ].values.every((it: any) => it.isSelected);
  }

  selectAllItems(): void {
    this.filteredDynamicLabelManagerItems[this.currentTab].isSelectAll =
      !this.filteredDynamicLabelManagerItems[this.currentTab].isSelectAll;
    if (this.filteredDynamicLabelManagerItems[this.currentTab].isSelectAll) {
      this.filteredDynamicLabelManagerItems[this.currentTab].values.forEach(item => {
        item.isSelected = true;
      });
    } else {
      this.filteredDynamicLabelManagerItems[this.currentTab].values.forEach(item => {
        item.isSelected = false;
      });
    }
  }

  saveUploadResults(): void {
    if ((this.uploadResult?.failed || 0) > 0) {
      this.toastrService.error('Please clear the failed items before saving.');
      return;
    }
    // First we need to check if the upload results are valid, if not then filtered them.
    this.checkValidUploadResults();

    this.dynamicLabelManagerMapper = { ...JSON.parse(JSON.stringify(this.filteredDynamicLabelManagerItems)) };
    this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
    this.calculateFailedItemsAndClearedItems();
    this.isEditMode = false;
  }

  checkValidUploadResults(): void {
    // Check for the tags first.
    this.filteredDynamicLabelManagerItems.tags.values = this.filteredDynamicLabelManagerItems.tags.values.filter(
      (item: any) => {
        return Object.values(item).some(value => {
          return value !== null && value !== undefined && value !== '';
        });
      }
    );

    // Check for the categories.
    this.filteredDynamicLabelManagerItems.categories.values =
      this.filteredDynamicLabelManagerItems.categories.values.filter((item: any) => {
        if (this.type === LabelType.POLICY) {
          if (
            item[DynamicLabelManagerSections.POLICY_CATEGORIES] === '' &&
            item[DynamicLabelManagerSections.POLICY_SUB_CATEGORIES] === ''
          ) {
            return false;
          } else {
            return true;
          }
        } else {
          return Object.values(item).some(value => {
            return value !== null && value !== undefined && value !== '';
          });
        }
      });
  }

  resetUploadResults(): void {
    this.checkValidUploadResults();
    this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
    this.isEditMode = false;

    // Need to write a helper function to calculate the failed items and cleared items.
    this.calculateFailedItemsAndClearedItems();
  }

  calculateFailedItemsAndClearedItems(): void {
    this.uploadResult.failed = 0;
    this.uploadResult.cleared = 0;
    this.uploadResult.failedItems = [];

    // Counts reflect only the currently active tab (categories or tags)
    if (this.currentTab === 'categories') {
      this.filteredDynamicLabelManagerItems?.categories?.values?.forEach((item: any, index: number) => {
        if (this.type === LabelType.POLICY) {
          if (item[DynamicLabelManagerSections.POLICY_CATEGORIES] === null) {
            this.uploadResult.failed++;
            item.rowNumber = index + 1;
            this.uploadResult.failedItems.push(item);
          } else {
            this.uploadResult.cleared++;
          }
        } else {
          if (Object.values(item).some((value: any) => !UtilsService.isEmpty(value as string))) {
            this.uploadResult.cleared++;
          }
        }
      });
    } else {
      this.filteredDynamicLabelManagerItems?.tags?.values?.forEach((item: any) => {
        if (Object.values(item).some((value: any) => !UtilsService.isEmpty(value as string))) {
          this.uploadResult.cleared++;
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions && !!this.subscriptions.length) {
      this.subscriptions.forEach(sub => sub.unsubscribe());
    }
  }

  toggleDropdown(item: any): void {
    // Close all other dropdowns first
    this.filteredDynamicLabelManagerItems[this.currentTab].values.forEach(i => {
      if (i !== item) {
        i.isDropdownOpen = false;
      }
    });

    item.isDropdownOpen = !item.isDropdownOpen;
    this.showAddBox = false;
    this.newTagValue = '';
  }

  closeDropdown(item: any): void {
    item.isDropdownOpen = false;
  }

  selectOption(item: any, header: any, option: any): void {
    // Need to check if the header.name is of POlicy Categories and its value before adding is null,
    // then need to update the failed items counter and array too.
    if (header.name === DynamicLabelManagerSections.POLICY_CATEGORIES && item[header.name] === null) {
      this.uploadResult.failed--;
      const isDropDownOpenIndex = this.filteredDynamicLabelManagerItems[this.currentTab].values.findIndex(
        labelItem => labelItem.isDropdownOpen
      );
      if (isDropDownOpenIndex !== -1) {
        this.uploadResult.failedItems.splice(isDropDownOpenIndex, 1);
      }
      this.uploadResult.cleared++;
    }
    item[header.name] = option.value;
    this.closeDropdown(item);
    this.showAddBox = false;
  }

  onAddNewCategory(): void {
    this.showAddBox = false;
    if (this.newTagValue !== null && this.newTagValue !== undefined && this.newTagValue !== '') {
      // Check in default categories if the category already exists.
      const isCategoryExists = this.defaultCategories.find(category => category.value === this.newTagValue);
      if (isCategoryExists) {
        this.toastrService.error('Category already exists');
        return;
      }
      this.defaultCategories.push({ name: this.newTagValue, value: this.newTagValue });
    }
    this.newTagValue = '';
  }

  openFailedItemsModal(): void {
    this.showFailedItemsModal = true;
  }

  closeFailedItemsModal(): void {
    this.showFailedItemsModal = false;
  }
}
